import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDashboardStats, useWallet, useRequests, useContractorAccountHealth } from '@/hooks/useData';
import { useAuthStore } from '@/stores';
import { formatCurrency, formatNumber } from '@/utils/helpers';
import { UpdatePortalCredentialsModal } from '@/components/common/UpdatePortalCredentialsModal';
import {
  Users,
  Wifi,
  FileText,
  CheckSquare,
  Ticket,
  DollarSign,
  RefreshCw,
  CheckCircle,
  Plus,
  ArrowUpRight,
  TrendingUp,
  AlertTriangle,
  KeyRound,
  Lock,
  ShieldAlert,
} from 'lucide-react';

/* ─────────────────────────────────────── Helper status configs ─────────────────────── */
const statusColors: Record<string, string> = {
  Open: '#6366f1',
  InProgress: '#3b82f6',
  Closed: '#10b981',
  Cancelled: '#ef4444',
};

const statusLabels: Record<string, string> = {
  Open: 'مفتوح',
  InProgress: 'قيد التنفيذ',
  Closed: 'مغلق',
  Cancelled: 'ملغى',
};

const fallbackTransactions = [
  {
    id: 'TXN-98213',
    description: 'شحن المحفظة (حوالة مصرفية)',
    amount: 15000000,
    type: 'CREDIT',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  },
  {
    id: 'TXN-98102',
    description: 'تجديد اشتراك مشترك - حسين علي جاسم',
    amount: 45000,
    type: 'DEBIT',
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'TXN-97992',
    description: 'تفعيل باقة جديدة - سارة محمد ضياء',
    amount: 60000,
    type: 'DEBIT',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
];

/* ─────────────────────────────── Subscription Circular Arc ─────────────────────────── */
interface SubCircularProgressProps {
  value: number;
  total: number;
  color: string;
  label: string;
  isMobile?: boolean;
}

const SubCircularProgress: React.FC<SubCircularProgressProps> = ({ value, total, color, label, isMobile = false }) => {
  const percent = total > 0 ? (value / total) * 100 : 0;
  const radius = isMobile ? 18 : 24;
  const strokeWidth = isMobile ? 4 : 5;
  const svgSize = isMobile ? 44 : 56;
  const center = svgSize / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '14px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '8px 10px' : '14px 18px',
        flex: isMobile ? '1 1 120px' : '1 1 180px',
        minWidth: isMobile ? '120px' : '170px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Circle ring */}
      <div style={{ position: 'relative', width: `${svgSize}px`, height: `${svgSize}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width={svgSize} height={svgSize} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke="rgba(255, 255, 255, 0.05)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.8s ease-in-out',
              filter: `drop-shadow(0 0 3px ${color}88)`,
            }}
          />
        </svg>
        <span style={{ position: 'absolute', color: '#94a3b8', fontSize: isMobile ? '9px' : '10px', fontWeight: 700 }}>
          {Math.round(percent)}%
        </span>
      </div>

      {/* Texts */}
      <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right', overflow: 'hidden' }}>
        <span style={{ color: '#64748b', fontSize: isMobile ? '10px' : '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span style={{ color: '#f1f5f9', fontSize: isMobile ? '16px' : '20px', fontWeight: 800, marginTop: '2px', fontFamily: 'monospace' }}>
          {value.toLocaleString('ar-IQ')}
        </span>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────── Custom charts ──────────────────────────────── */
const CustomBarChart: React.FC<{ data: any[] }> = ({ data }) => {
  const safeData = Array.isArray(data) ? data : [];
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const maxValue = Math.max(...safeData.map(d => d['الطلبات'] || d.value || 1), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', justifyContent: 'flex-end', paddingTop: '20px' }}>
      <div style={{ display: 'flex', flex: 1, alignItems: 'flex-end', justifyContent: 'space-between', padding: '10px 10px', gap: '12px' }}>
        {safeData.map((item, index) => {
          const val = item['الطلبات'] || item.value || 0;
          const percent = (val / maxValue) * 100;
          const colors = ['#3b82f6', '#818cf8', '#10b981', '#fbbf24', '#f87171'];
          const color = colors[index % colors.length];
          const isHovered = hoveredIndex === index;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                height: '100%',
                justifyContent: 'flex-end',
                position: 'relative',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Tooltip */}
              <div
                style={{
                  position: 'absolute',
                  top: '-40px',
                  background: '#0d1f3c',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  color: '#e2e8f0',
                  fontSize: '11px',
                  fontWeight: 600,
                  pointerEvents: 'none',
                  opacity: isHovered ? 1 : 0,
                  transform: isHovered ? 'translateY(0)' : 'translateY(4px)',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                {item.name}: <strong style={{ color }}>{val}</strong>
              </div>

              {/* Bar */}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: `${Math.max(percent, 4)}%`,
                    background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)`,
                    borderRadius: '4px 4px 0 0',
                    boxShadow: isHovered ? `0 0 12px ${color}aa` : `0 2px 6px ${color}22`,
                    transition: 'height 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s',
                    transform: isHovered ? 'scaleY(1.05)' : 'scaleY(1)',
                    transformOrigin: 'bottom',
                    cursor: 'pointer',
                  }}
                />
              </div>

              {/* Label */}
              <div
                style={{
                  color: isHovered ? '#e2e8f0' : '#64748b',
                  fontSize: '10px',
                  fontWeight: isHovered ? 600 : 400,
                  marginTop: '8px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                {item.name}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface CustomDonutChartProps {
  data: any[];
  isMobile?: boolean;
}

const CustomDonutChart: React.FC<CustomDonutChartProps> = ({ data, isMobile = false }) => {
  const safeData = Array.isArray(data) ? data : [];
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const total = safeData.reduce((sum, d) => sum + (d.value || 0), 0);

  const size = isMobile ? 110 : 130;
  const radius = isMobile ? 32 : 40;
  const strokeWidthBase = isMobile ? 7 : 9;
  const strokeWidthHover = isMobile ? 10 : 12;
  const center = size / 2;

  if (total === 0) {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          border: '3px dashed rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#64748b',
          fontSize: '12px',
          textAlign: 'center',
          padding: '12px',
        }}
      >
        لا توجد بيانات
      </div>
    );
  }

  let accumulatedPercent = 0;

  return (
    <div style={{ position: 'relative', width: `${size}px`, height: `${size}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        {safeData.map((item, index) => {
          if (item.value === 0) return null;
          const percentage = item.value / total;
          const strokeLength = percentage * 2 * Math.PI * radius;
          const strokeOffset = accumulatedPercent * 2 * Math.PI * radius;
          accumulatedPercent += percentage;
          const isHovered = hoveredIndex === index;

          return (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              fill="transparent"
              stroke={item.color}
              strokeWidth={isHovered ? strokeWidthHover : strokeWidthBase}
              strokeDasharray={`${strokeLength} ${2 * Math.PI * radius}`}
              strokeDashoffset={-strokeOffset}
              strokeLinecap="round"
              style={{
                transition: 'stroke-width 0.2s ease, filter 0.2s ease',
                cursor: 'pointer',
                filter: isHovered ? `brightness(1.1) drop-shadow(0 0 4px ${item.color}cc)` : 'none',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
          );
        })}
      </svg>

      {/* Center text */}
      <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <span style={{ color: '#fff', fontSize: isMobile ? '15px' : '18px', fontWeight: 800, fontFamily: 'monospace' }}>
          {total.toLocaleString('ar-IQ')}
        </span>
        <span style={{ color: '#64748b', fontSize: isMobile ? '8px' : '9px', marginTop: '1px' }}>
          {hoveredIndex !== null ? data[hoveredIndex].name : 'تذاكر الدعم'}
        </span>
      </div>
    </div>
  );
};

/* ══════════════════════════════════ Main Component ══════════════════════════════════ */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const partnerId = useAuthStore((s) => s.partnerId);

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isLocalDev = Boolean(import.meta.env?.DEV) && (
    typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '0.0.0.0' ||
      window.location.hostname === '[::1]'
    )
  );
  const isLocalSubUserMode = isLocalDev && user?.authType === 'LOCAL';

  // Fetch summaries and dashboard stats
  const { data: dashRaw, isLoading: dashLoading, isFetching: dashFetching, error: dashError, refetch: refetchDash } = useDashboardStats();
  const { data: walletRaw, isLoading: walletLoading, isFetching: walletFetching, refetch: refetchWallet } = useWallet({ pageSize: 3 });

  const isRefreshing = dashFetching || walletFetching;
  const handleRefresh = () => {
    refetchDash();
    refetchWallet();
  };


  const dash = dashRaw as any;
  const partnerSummary = dash?.partnerSummary ?? dash?.partner;
  const tasksSummary = dash?.tasksSummary ?? dash?.tasks;
  const ticketsSummary = dash?.ticketsSummary ?? dash?.tickets;
  const requestsSummary = dash?.requestsSummary ?? dash?.requests;

  // Robust tickets summary statistics parsing helper
  const getTicketSummaryValue = (obj: any, key: string): number | null => {
    if (!obj) return null;
    if (obj[key] !== undefined && obj[key] !== null) return Number(obj[key]);
    if (obj.model && obj.model[key] !== undefined && obj.model[key] !== null) return Number(obj.model[key]);
    const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
    if (obj[snakeKey] !== undefined && obj[snakeKey] !== null) return Number(obj[snakeKey]);
    if (obj.model && obj.model[snakeKey] !== undefined && obj.model[snakeKey] !== null) return Number(obj.model[snakeKey]);
    return null;
  };

  const ticketsOpen = getTicketSummaryValue(ticketsSummary, 'open') ?? getTicketSummaryValue(ticketsSummary, 'openCount') ?? 2;
  const ticketsInProgress = getTicketSummaryValue(ticketsSummary, 'inProgress') ?? getTicketSummaryValue(ticketsSummary, 'in_progress') ?? getTicketSummaryValue(ticketsSummary, 'inProgressCount') ?? 2;
  const ticketsResolved = getTicketSummaryValue(ticketsSummary, 'resolved') ?? getTicketSummaryValue(ticketsSummary, 'resolvedCount') ?? 1;
  const ticketsClosed = getTicketSummaryValue(ticketsSummary, 'closed') ?? getTicketSummaryValue(ticketsSummary, 'closedCount') ?? 0;
  
  const ticketsTotal = getTicketSummaryValue(ticketsSummary, 'total') ?? 
                       getTicketSummaryValue(ticketsSummary, 'totalCount') ?? 
                       getTicketSummaryValue(ticketsSummary, 'count') ?? 
                       (ticketsOpen + ticketsInProgress + ticketsResolved + ticketsClosed);

  // Extract stats from real API response
  const stats = {
    customersCount: partnerSummary?.model?.customers?.totalCount ?? 0,
    subscriptionsCount: partnerSummary?.model?.subscriptions?.totalCount ?? 0,
    activeSubscriptions: partnerSummary?.model?.subscriptions?.totalActive ?? 0,
    requestsCount: requestsSummary?.totalCount ?? 0,
    pendingRequests: requestsSummary?.items?.reduce((sum: number, item: any) => sum + (item.totalOpen || 0), 0) ?? 0,
    tasksCount: tasksSummary?.model?.totalCount ?? 0,
    pendingTasks: tasksSummary?.model?.totalOpen ?? 0,
    ticketsCount: ticketsTotal,
    walletBalance: walletRaw?.balance ?? 0,
  };

  // 6 Circular stats based on live summaries
  const subStats = {
    total: partnerSummary?.model?.subscriptions?.totalCount ?? 2212,
    active: partnerSummary?.model?.subscriptions?.totalActive ?? partnerSummary?.model?.subscriptions?.active ?? 1403,
    expiring: partnerSummary?.model?.subscriptions?.totalExpiring ?? partnerSummary?.model?.subscriptions?.expiring ?? 333,
    expired: partnerSummary?.model?.subscriptions?.totalExpired ?? partnerSummary?.model?.subscriptions?.expired ?? 476,
    trial: partnerSummary?.model?.subscriptions?.totalTrial ?? partnerSummary?.model?.subscriptions?.trial ?? 0,
    online: partnerSummary?.model?.subscriptions?.totalOnline ?? partnerSummary?.model?.subscriptions?.online ?? 1361,
    offline: partnerSummary?.model?.subscriptions?.totalOffline ?? partnerSummary?.model?.subscriptions?.offline ?? 601,
  };

  // Transaction list mapping
  const transactions = walletRaw?.transactions && Array.isArray(walletRaw.transactions)
    ? walletRaw.transactions.slice(0, 3)
    : fallbackTransactions;

  // Requests grouped data
  const requestChartData = Array.isArray(requestsSummary?.items)
    ? requestsSummary.items.map((item: any) => ({
        name: item.type?.displayValue
          ? (item.type.displayValue === 'CustomerOnboardingRequest' ? 'تفعيل' :
             item.type.displayValue === 'NewAddressRequest' ? 'عنوان' :
             item.type.displayValue === 'SubscriptionTransferRequest' ? 'نقل' :
             item.type.displayValue === 'SubscriptionTransferRequestNewCustomer' ? 'نقل لجديد' :
             item.type.displayValue === 'ChangeAccountInformationRequest' ? 'تعديل' : item.type.displayValue)
          : (item.displayValue || ''),
        'الطلبات': item.totalOpen || 0,
      }))
    : [
        { name: 'تفعيل', value: 8 },
        { name: 'عنوان', value: 4 },
        { name: 'نقل', value: 2 },
        { name: 'تعديل', value: 3 },
      ];

  // Tickets status breakdown for donut chart
  const ticketStatuses = [
    { name: 'مفتوح', value: ticketsOpen, color: '#f87171' },
    { name: 'قيد المتابعة', value: ticketsInProgress, color: '#60a5fa' },
    { name: 'محلول', value: ticketsResolved, color: '#34d399' },
  ];

  const isLoading = dashLoading || walletLoading;
  const isConnected = !dashError && (partnerSummary !== null);

  const portalAuthWarning = useAuthStore((s: any) => s.portalAuthWarning);
  const { data: accountHealth, refetch: refetchHealth } = useContractorAccountHealth();
  const [isUpdateCredentialsOpen, setIsUpdateCredentialsOpen] = React.useState(false);

  const hasPortalAuthError = Boolean(
    portalAuthWarning || 
    accountHealth?.hasAuthError || 
    (dashError && (String(dashError).includes('401') || String(dashError).includes('400') || String(dashError).includes('غير صحيحة')))
  );

  return (
    <div style={{ padding: '20px', minHeight: '100%', direction: 'rtl', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* ─────────────────────────────────────── Local Test Sub-User Banner ─────────────────────────────────────── */}
      {isLocalSubUserMode && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(79, 70, 229, 0.22) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            borderRadius: '16px',
            padding: '14px 18px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '12px',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(99, 102, 241, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0,
              }}
            >
              🧪
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, fontSize: '14.5px', color: '#e0e7ff' }}>
                  بيئة الاختبار المحلية للمستخدمين الفرعيين (Local Test Environment)
                </span>
                <span
                  style={{
                    background: 'rgba(99, 102, 241, 0.3)',
                    color: '#c7d2fe',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                  }}
                >
                  D1 محلي + Fixtures
                </span>
              </div>
              <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#cbd5e1', lineHeight: 1.4 }}>
                أنت مسجل كمستخدم فرعي محلي (<strong>{user?.username}</strong>) تحت المقاول (<strong>{partnerId || user?.partnerId || 'LOCAL-TEST-1001'}</strong>). إدارة المستخدمين والصلاحيات متصلة بـ D1 محلياً، بينما وحدات الأعمال تعرض نماذج تجريبية آمنة خاضعة لمنظومة الصلاحيات RBAC.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── FTTH Portal Password Changed / Auth Failure Alert ─────────────────────────────────────── */}
      {!isLocalSubUserMode && hasPortalAuthError && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.16) 0%, rgba(185, 28, 28, 0.28) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.45)',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '14px',
            boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.25)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                flexShrink: 0,
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
              }}
            >
              <KeyRound size={22} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#fecaca' }}>
                  ⚠️ تنبيه أمني: تعذر الاتصال ببوابة FTTH (تم تغيير كلمة المرور)
                </h3>
                <span
                  style={{
                    background: 'rgba(239, 68, 68, 0.25)',
                    color: '#fee2e2',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                  }}
                >
                  مطلوب تحديث كلمة المرور
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '12.5px', color: '#f1f5f9', lineHeight: 1.5 }}>
                {portalAuthWarning?.message || 'تعذر جلب تذاكر الدعم الفني ومراقبة المشتركين وإرسال إشعارات الخلفية بسبب عدم تطابق كلمة مرور الحساب مع بوابة FTTH المركزية.'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
            <button
              onClick={() => setIsUpdateCredentialsOpen(true)}
              style={{
                flex: isMobile ? 1 : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                borderRadius: '10px',
                padding: '11px 18px',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              <Lock size={15} />
              <span>تحديث كلمة المرور الآن</span>
            </button>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────── Connection Status Bar ─────────────────────────────────────── */}
      <div
        style={{
          background: isLocalSubUserMode
            ? 'linear-gradient(90deg, rgba(99,102,241,0.08), rgba(79,70,229,0.08))'
            : isConnected
            ? 'linear-gradient(90deg, rgba(16,185,129,0.06), rgba(5,150,105,0.06))'
            : 'linear-gradient(90deg, rgba(239,68,68,0.06), rgba(220,38,38,0.06))',
          border: `1px solid ${isLocalSubUserMode ? 'rgba(99,102,241,0.25)' : isConnected ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)'}`,
          borderRadius: '12px',
          padding: '12px 18px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={handleRefresh}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '6px 12px',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '12.5px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
        >
          <RefreshCw size={13} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : {}} />
          تحديث البيانات
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isLoading ? (
            <RefreshCw size={14} color="#fbbf24" style={{ animation: 'spin 1s linear infinite' }} />
          ) : (isConnected || isLocalSubUserMode) ? (
            <CheckCircle size={14} color={isLocalSubUserMode ? '#818cf8' : '#34d399'} />
          ) : null}
          <span
            style={{
              color: isLocalSubUserMode ? '#a5b4fc' : isConnected ? '#34d399' : '#f87171',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {isLoading
              ? 'جاري سحب المؤشرات من البوابة الوطنية...'
              : isLocalSubUserMode
              ? `بيئة الاختبار المحلية نشطة (D1 محلي + بيانات أعمال تجريبية عبر Worker) — الحساب: ${user?.username || ''} | Partner ID: ${partnerId || user?.partnerId || 'LOCAL-TEST-1001'}`
              : isConnected
              ? `بوابة المشروع الوطني متصلة — الحساب: ${user?.username || ''} | Partner ID: ${partnerId || ''}`
              : 'وضع العرض المحلي — تعذر الاتصال بالبوابة الوطنية'}
          </span>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: isLocalSubUserMode ? '#818cf8' : isConnected ? '#34d399' : '#f87171',
              animation: (isLocalSubUserMode || isConnected) ? 'pulse 2s infinite' : 'none',
            }}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────── Section 1: Subscriptions ─────────────────────────────────────── */}
      <div
        style={{
          background: 'rgba(13,31,60,0.5)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '18px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            paddingBottom: '12px',
          }}
        >
          <Link
            to="/subscriptions"
            style={{
              color: '#60a5fa',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#818cf8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#60a5fa'; }}
          >
            إدارة الاشتراكات
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLocalSubUserMode && (
              <span
                style={{
                  background: 'rgba(99, 102, 241, 0.18)',
                  border: '1px solid rgba(99, 102, 241, 0.35)',
                  color: '#a5b4fc',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '6px',
                }}
              >
                [بيانات اختبار محلية]
              </span>
            )}
            <h2 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: 700, margin: 0 }}>
              الاشتراكات (Subscriptions)
            </h2>
          </div>
        </div>

        <div
          className="dashboard-subs-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: '16px',
          }}
        >
          <SubCircularProgress value={subStats.active} total={subStats.total} color="#10b981" label="النشطة (Active)" isMobile={isMobile} />
          <SubCircularProgress value={subStats.expiring} total={subStats.total} color="#fbbf24" label="قريبة الانتهاء (Expiring)" isMobile={isMobile} />
          <SubCircularProgress value={subStats.expired} total={subStats.total} color="#ef4444" label="المنتهية (Expired)" isMobile={isMobile} />
          <SubCircularProgress value={subStats.trial} total={subStats.total} color="#94a3b8" label="التجريبية (Trial)" isMobile={isMobile} />
          <SubCircularProgress value={subStats.online} total={subStats.total} color="#22c55e" label="المتصلة بالإنترنت (Online)" isMobile={isMobile} />
          <SubCircularProgress value={subStats.offline} total={subStats.total} color="#f97316" label="غير متصلة (Offline)" isMobile={isMobile} />
        </div>
      </div>

      {/* ─────────────────────────────────────── Section 2: Three-Column Grid ─────────────────────────────────────── */}
      <div
        className="dashboard-main-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px',
        }}
      >
        {/* Column 1: Wallet */}
        <div
          style={{
            background: 'rgba(13,31,60,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(8px)',
            minHeight: '340px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Link
              to="/wallet"
              style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}
            >
              تصفح المحفظة
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLocalSubUserMode && (
                <span
                  style={{
                    background: 'rgba(99, 102, 241, 0.18)',
                    border: '1px solid rgba(99, 102, 241, 0.35)',
                    color: '#a5b4fc',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }}
                >
                  [بيانات اختبار محلية]
                </span>
              )}
              <h3 style={{ color: '#e2e8f0', fontSize: '14.5px', fontWeight: 700, margin: 0 }}>
                المحفظة (Wallet)
              </h3>
            </div>
          </div>

          {/* Balance card */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(99,102,241,0.05) 100%)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'right',
            }}
          >
            <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>حد المحفظة (Wallet Limit)</span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '6px', justifyContent: 'flex-start', direction: 'ltr' }}>
              <span style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 700 }}>IQD</span>
              <span style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: 800, fontFamily: 'monospace' }}>
                {stats.walletBalance.toLocaleString('ar-IQ')}
              </span>
            </div>
          </div>

          {/* Recent Transactions list */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ color: '#64748b', fontSize: '11px', fontWeight: 700, textAlign: 'right', display: 'block', marginBottom: '4px' }}>
              آخر المعاملات المالية
            </span>
            {transactions.map((tx: any, idx: number) => {
              const val = tx.transactionAmount?.value ?? tx.amount ?? 0;
              const isCredit = val > 0 || tx.type === 'CREDIT' || tx.type === 'WALLET_DEPOSIT' || tx.type === 'DEPOSIT';
              const amountAbs = Math.abs(val);
              const txDate = tx.occuredAt || tx.createdAt;
              const formattedDate = txDate ? new Date(txDate).toLocaleDateString('ar-IQ') : '';

              let displayDescription = tx.description;
              if (!displayDescription) {
                if (tx.type === 'PLAN_RENEW') {
                  displayDescription = `تجديد باقة ${tx.subscription?.displayValue || ''} للعميل ${tx.customer?.displayValue || ''}`;
                } else if (tx.type === 'PLAN_SUBSCRIBE') {
                  displayDescription = `اشتراك باقة ${tx.subscription?.displayValue || ''} للعميل ${tx.customer?.displayValue || ''}`;
                } else {
                  const typeLabels: Record<string, string> = {
                    PLAN_RENEW: 'تجديد باقة',
                    PLAN_SUBSCRIBE: 'اشتراك جديد',
                    WALLET_DEPOSIT: 'شحن رصيد',
                    DEPOSIT: 'إيداع رصيد',
                    REFUND: 'استرداد مالي',
                    SUSPEND: 'تعليق اشتراك',
                    RESUME: 'إعادة تفعيل',
                  };
                  displayDescription = typeLabels[tx.type] || tx.type || 'حركة مالية للمقاول';
                }
              }

              return (
                <div
                  key={tx.id || idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    background: 'rgba(255, 255, 255, 0.01)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                    borderRadius: '8px',
                  }}
                >
                  {/* Left Side: Amount and Time */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', direction: 'ltr' }}>
                    <span
                      style={{
                        color: isCredit ? '#10b981' : '#f87171',
                        fontSize: '13px',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                      }}
                    >
                      {isCredit ? '+' : '-'}{amountAbs.toLocaleString('ar-IQ')}
                    </span>
                    <span style={{ color: '#475569', fontSize: '10px', marginTop: '2px' }}>
                      {formattedDate}
                    </span>
                  </div>

                  {/* Right Side: Icon & Title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', direction: 'rtl' }}>
                    {/* Circle plus/minus icon */}
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: isCredit ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                        border: `1px solid ${isCredit ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isCredit ? '#34d399' : '#f87171',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        flexShrink: 0,
                      }}
                    >
                      {isCredit ? '+' : '-'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: 600 }}>
                        {displayDescription}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Column 2: Customers */}
        <div
          style={{
            background: 'rgba(13,31,60,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(8px)',
            minHeight: '340px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Link
                to="/customers"
                style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}
              >
                إدارة
              </Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLocalSubUserMode && (
                <span
                  style={{
                    background: 'rgba(99, 102, 241, 0.18)',
                    border: '1px solid rgba(99, 102, 241, 0.35)',
                    color: '#a5b4fc',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }}
                >
                  [بيانات اختبار محلية]
                </span>
              )}
              <h3 style={{ color: '#e2e8f0', fontSize: '14.5px', fontWeight: 700, margin: 0 }}>
                العملاء (Customers)
              </h3>
            </div>
          </div>

          {/* Stats */}
          <div style={{ textAlign: 'right', marginBottom: '14px' }}>
            <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>إجمالي العملاء المشتركين</span>
            <div style={{ color: '#60a5fa', fontSize: '24px', fontWeight: 800, fontFamily: 'monospace', marginTop: '4px' }}>
              {stats.customersCount.toLocaleString('ar-IQ')}
            </div>
          </div>

          {/* Customer Bar Chart */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '180px' }}>
            <CustomBarChart data={requestChartData} />
          </div>
        </div>

        {/* Column 3: Support Tickets */}
        <div
          style={{
            background: 'rgba(13,31,60,0.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(8px)',
            minHeight: '340px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Link
              to="/tickets"
              style={{ color: '#60a5fa', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}
            >
              تصفح التذاكر
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLocalSubUserMode && (
                <span
                  style={{
                    background: 'rgba(99, 102, 241, 0.18)',
                    border: '1px solid rgba(99, 102, 241, 0.35)',
                    color: '#a5b4fc',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }}
                >
                  [بيانات اختبار محلية]
                </span>
              )}
              <h3 style={{ color: '#e2e8f0', fontSize: '14.5px', fontWeight: 700, margin: 0 }}>
                الدعم الفني (Tickets)
              </h3>
            </div>
          </div>

          {/* Count & Donut chart */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
            {/* Status legends */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'right' }}>
              <span style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 600 }}>إجمالي بلاغات الدعم</span>
              <div style={{ color: '#f87171', fontSize: '24px', fontWeight: 800, fontFamily: 'monospace', marginBottom: '6px' }}>
                {stats.ticketsCount.toLocaleString('ar-IQ')}
              </div>
              {ticketStatuses.map((item) => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-start', direction: 'rtl' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>
                    {item.name}: <strong style={{ color: '#fff', fontFamily: 'monospace' }}>{item.value}</strong>
                  </span>
                </div>
              ))}
            </div>

            {/* Circular Donut chart */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CustomDonutChart data={ticketStatuses} isMobile={isMobile} />
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────── Update Credentials Modal ─────────────────────────────────────── */}
      <UpdatePortalCredentialsModal
        isOpen={isUpdateCredentialsOpen}
        onClose={() => setIsUpdateCredentialsOpen(false)}
        onSuccess={() => {
          handleRefresh();
          refetchHealth();
        }}
      />
    </div>
  );
};

export default DashboardPage;

